/**
 * AplicarSaldoFavorDialog — usa el saldo a favor de la agencia en esta reserva.
 *
 * No es un pago nuevo: es la parte que quedó sin repartir de una transferencia
 * que la agencia ya hizo. Por eso el diálogo muestra de dónde sale la plata
 * —qué comprobante y de qué reserva— antes de aplicarla.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, TextField, Alert, Stack,
} from '@mui/material';
import SavingsIcon from '@mui/icons-material/Savings';
import { API_BASE, getAuthHeaders, formatPrecio } from './shared/format';

export default function AplicarSaldoFavorDialog({
  open, onClose, reserva, disponible = 0, origenes = [], onHecho, onToast,
}) {
  const saldo = Number(reserva?.saldo_pendiente || 0);
  const tope = Math.min(saldo, Number(disponible || 0));
  const [monto, setMonto] = useState(String(tope));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMonto(String(Math.min(Number(reserva?.saldo_pendiente || 0), Number(disponible || 0))));
    setError(null);
  }, [open, reserva?.saldo_pendiente, disponible]);

  const montoNum = Number(String(monto).replace(/[^\d.]/g, '')) || 0;
  const excede = montoNum > tope;
  const queda = Math.max(saldo - montoNum, 0);
  const restante = Math.max(Number(disponible || 0) - montoNum, 0);

  const aplicar = async () => {
    setGuardando(true); setError(null);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/correcciones/reservas/${reserva.id}/aplicar-saldo-a-favor`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({ monto: montoNum }),
        });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.message || d.detail || 'No se pudo aplicar');
      onToast?.success?.(queda > 0
        ? `Aplicados ${formatPrecio(montoNum, reserva.moneda)}. Quedan ${formatPrecio(queda, reserva.moneda)} por pagar.`
        : 'Saldo a favor aplicado. La reserva queda sin saldo.');
      onHecho?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'No se pudo aplicar');
    }
    setGuardando(false);
  };

  return (
    <Dialog open={!!open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>
        Aplicar saldo a favor
        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
          {reserva?.codigo_reserva} · debe {formatPrecio(saldo, reserva?.moneda)} ·
          {' '}disponible {formatPrecio(disponible, reserva?.moneda)}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {/* De dónde sale la plata. Sin esto el operador aplica a ciegas. */}
          {origenes.length > 0 && (
            <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#f0fdf4',
                       border: '1px solid #bbf7d0' }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#15803d',
                                textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.5 }}>
                De dónde sale
              </Typography>
              {origenes.map(o => (
                <Typography key={o.pago_recibido_id} sx={{ fontSize: 11.5, color: '#166534' }}>
                  • {formatPrecio(o.disponible, reserva?.moneda)} · {o.fecha_pago || 's/f'}
                  {o.referencia ? ` · ref ${o.referencia}` : ''}
                  {o.origen_codigo_reserva ? ` · excedente de ${o.origen_codigo_reserva}` : ''}
                </Typography>
              ))}
            </Box>
          )}

          <TextField label="Cuánto aplicar" size="small" value={monto}
                     onChange={(e) => setMonto(e.target.value)} error={excede}
                     helperText={excede
                       ? `No puede pasar de ${formatPrecio(tope, reserva?.moneda)}`
                       : 'Puede aplicarse parcial'} />

          <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 12, color: '#475569' }}>
              La reserva queda debiendo <b>{formatPrecio(queda, reserva?.moneda)}</b>
              {' '}· a la agencia le quedan <b>{formatPrecio(restante, reserva?.moneda)}</b> a favor.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button variant="contained" startIcon={<SavingsIcon />}
                disabled={excede || montoNum <= 0 || guardando} onClick={aplicar}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#15803d',
                      '&:hover': { bgcolor: '#166534' } }}>
          {guardando ? 'Aplicando…' : 'Aplicar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
